import type { FastifyPluginAsync } from 'fastify'
import { costTracker } from '../../services/cost-tracker.service.js'
import { createLogger } from '../../common/utils/logger.js'

const logger = createLogger('AdminCostController')

export const adminCostRoutes: FastifyPluginAsync = async (fastify) => {
  
  /**
   * GET /admin/cost/analytics
   * Get comprehensive cost analytics
   */
  fastify.get('/analytics', {
    schema: {
      querystring: {
        type: 'object',
        properties: {
          period: { type: 'string', enum: ['day', 'week', 'month'] }
        }
      }
    }
  }, async (request, reply) => {
    try {
      const { period = 'month' } = request.query as { period?: 'day' | 'week' | 'month' }
      const analytics = await costTracker.getAnalytics(period)
      
      return reply.status(200).send({
        success: true,
        data: analytics
      })
    } catch (error) {
      logger.error('[GET /analytics] Failed', { error })
      return reply.status(500).send({
        success: false,
        error: 'Failed to get cost analytics'
      })
    }
  })

  /**
   * GET /admin/cost/summary
   * Get cost summary for a date range
   */
  fastify.get('/summary', {
    schema: {
      querystring: {
        type: 'object',
        properties: {
          startDate: { type: 'string' },
          endDate: { type: 'string' },
          provider: { type: 'string' },
          userId: { type: 'string' }
        },
        required: ['startDate', 'endDate']
      }
    }
  }, async (request, reply) => {
    try {
      const { startDate, endDate, provider, userId } = request.query as {
        startDate: string
        endDate: string
        provider?: string
        userId?: string
      }

      const summary = await costTracker.getSummary({
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        provider: provider as any,
        userId
      })

      return reply.status(200).send({
        success: true,
        data: summary
      })
    } catch (error) {
      logger.error('[GET /summary] Failed', { error })
      return reply.status(500).send({
        success: false,
        error: 'Failed to get cost summary'
      })
    }
  })

  /**
   * GET /admin/cost/budgets
   * Get active budgets
   */
  fastify.get('/budgets', async (request, reply) => {
    try {
      const budgets = await costTracker.getBudgets()
      
      return reply.status(200).send({
        success: true,
        data: budgets
      })
    } catch (error) {
      logger.error('[GET /budgets] Failed', { error })
      return reply.status(500).send({
        success: false,
        error: 'Failed to get budgets'
      })
    }
  })

  /**
   * POST /admin/cost/budgets
   * Create a new budget
   */
  fastify.post('/budgets', {
    schema: {
      body: {
        type: 'object',
        properties: {
          provider: { type: 'string' },
          limit: { type: 'number' },
          alertThreshold: { type: 'number' },
          period: { type: 'string', enum: ['daily', 'weekly', 'monthly'] },
          enabled: { type: 'boolean' }
        },
        required: ['provider', 'limit', 'alertThreshold', 'period']
      }
    }
  }, async (request, reply) => {
    // Budget creation to be implemented
    return reply.status(501).send({
      success: false,
      error: 'Budget creation not yet implemented'
    })
  })

  /**
   * GET /admin/cost/alerts
   * Get active cost alerts
   */
  fastify.get('/alerts', async (request, reply) => {
    try {
      const alerts = await costTracker.checkBudgets()
      
      return reply.status(200).send({
        success: true,
        data: alerts
      })
    } catch (error) {
      logger.error('[GET /alerts] Failed', { error })
      return reply.status(500).send({
        success: false,
        error: 'Failed to get alerts'
      })
    }
  })

  /**
   * POST /admin/cost/track
   * Manually track a cost entry (for testing)
   */
  fastify.post('/track', {
    schema: {
      body: {
        type: 'object',
        properties: {
          provider: { type: 'string' },
          service: { type: 'string' },
          cost: { type: 'number' },
          usage: { type: 'number' },
          unit: { type: 'string' },
          userId: { type: 'string' },
          feature: { type: 'string' }
        },
        required: ['provider', 'service', 'cost', 'usage', 'unit']
      }
    }
  }, async (request, reply) => {
    try {
      const params = request.body as any
      await costTracker.track(params)
      
      return reply.status(200).send({
        success: true,
        message: 'Cost tracked'
      })
    } catch (error) {
      logger.error('[POST /track] Failed', { error })
      return reply.status(500).send({
        success: false,
        error: 'Failed to track cost'
      })
    }
  })
}
